import React from 'react';
import styled from 'styled-components';
import { FiArrowLeft, FiArrowRight } from 'react-icons/fi';

interface SuccessStoriesProps {
  onHomeClick?: () => void;
}

const SuccessStoriesSection = styled.section`
  padding: 4rem 0;
  background: #f8fafc;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1rem;
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: #64748b;
  max-width: 600px;
  margin: 0 auto;
`;

const StoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
`;

const StoryCard = styled.div`
  background: white;
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 1px solid #e2e8f0;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  }
`;

const StoryHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
`;

const StudentAvatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 1.2rem;
  margin-right: 1rem;
`;

const StudentInfo = styled.div`
  flex: 1;
`;

const StudentName = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
`;

const StudentGrade = styled.p`
  font-size: 0.9rem;
  color: #64748b;
  margin: 0;
`;

const StoryContent = styled.p`
  color: #475569;
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const StoryStats = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
`;

const StatItem = styled.div`
  text-align: center;

  .number {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1e3a8a;
    display: block;
  }

  .label {
    font-size: 0.8rem;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const CTAButton = styled.button`
  background: linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: block;
  margin: 0 auto;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(30, 58, 138, 0.3);
  }
`;

const SuccessStories: React.FC<SuccessStoriesProps> = ({ onHomeClick }) => {
  const stories = [
    {
      id: 1,
      name: 'Sarah Johnson',
      grade: 'Grade 10',
      avatar: 'SJ',
      content: 'MathKnots completely transformed my understanding of algebra. The interactive lessons and practice problems made complex concepts easy to grasp.',
      improvement: '85%',
      timeSpent: '3 months'
    },
    {
      id: 2,
      name: 'Michael Chen',
      grade: 'Grade 8',
      avatar: 'MC',
      content: 'I was struggling with geometry until I found MathKnots. The video explanations and step-by-step solutions helped me build confidence.',
      improvement: '92%',
      timeSpent: '4 months'
    },
    {
      id: 3,
      name: 'Emily Rodriguez',
      grade: 'Grade 11',
      avatar: 'ER',
      content: 'The combination of books and videos is perfect for my learning style. I can review concepts multiple times until I understand them completely.',
      improvement: '78%',
      timeSpent: '6 months'
    }
  ];

  return (
    <SuccessStoriesSection id="success-stories">
      <Container>
        <SectionHeader>
          <SectionTitle>Success Stories</SectionTitle>
          <SectionSubtitle>
            See how MathKnots has helped students achieve their mathematical goals
          </SectionSubtitle>
        </SectionHeader>

        <StoriesGrid>
          {stories.map((story) => (
            <StoryCard key={story.id}>
              <StoryHeader>
                <StudentAvatar>{story.avatar}</StudentAvatar>
                <StudentInfo>
                  <StudentName>{story.name}</StudentName>
                  <StudentGrade>{story.grade}</StudentGrade>
                </StudentInfo>
              </StoryHeader>
              <StoryContent>{story.content}</StoryContent>
              <StoryStats>
                <StatItem>
                  <span className="number">{story.improvement}</span>
                  <span className="label">Improvement</span>
                </StatItem>
                <StatItem>
                  <span className="number">{story.timeSpent}</span>
                  <span className="label">Time</span>
                </StatItem>
              </StoryStats>
            </StoryCard>
          ))}
        </StoriesGrid>

        <CTAButton onClick={onHomeClick}>
          Start Your Success Story
        </CTAButton>
      </Container>
    </SuccessStoriesSection>
  );
};

export default SuccessStories; 